import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, ViewChild, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, map, tap } from 'rxjs';
import { ChangePasswordModalComponent } from 'src/app/layout/change-password-modal/change-password-modal.component';
import { DeviceType } from 'src/app/shared/enums/device-type.enum';
import { FeatureFlags, ClientPermission } from 'src/app/shared/enums/generated.enums';
import { AuthService } from 'src/app/shared/services/auth.service';
import { ModalService } from 'src/app/shared/services/modal.service';
import { NavigationService } from 'src/app/shared/services/navigation.service';
import { OrganizationService } from 'src/app/shared/services/organization.service';
import { StorageService } from 'src/app/shared/services/storage.service';
import { TenantsService } from 'src/app/shared/services/tenants.service';
import { Global } from 'src/app/shared/types/global.type';
import { LoggedUser } from 'src/app/shared/types/logged-user.type';
import { BaseModel } from 'src/app/shared/types/generated.types';
import { CustomSelectModalComponent } from 'src/app/shared/components/_modals/custom-select-modal/custom-select-modal.component';
import { LoadingScreenService } from 'src/app/shared/services/loading-screen.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Tenants } from 'src/app/shared/enums/page-type.enum';
import { NotificationSettingsModalComponent } from '../../shared/components/_modals/notification-settings-modal/notification-settings-modal.component';
import { MenuService } from '../../shared/services/menu.service';
import { MenuItem, MenuSettings } from '../../shared/types/menu-section.type';

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MenuComponent {
  private readonly router = inject(Router);
  
  readonly allTenants: BaseModel = { id: Tenants.AllTenants, label: 'All tenants' };
  readonly allWorkflows: BaseModel = { id: Tenants.AllWorkflows, label: 'All workflows' };

  menuSettings$: Observable<MenuSettings> = this.menuService.menuSettings$.pipe(
    tap(m => {
      this.allowExpandMenu = m.isMenuExpanded !== false;
      this.isMenuExpanded = m.isMenuExpanded ?? this.isMenuExpanded;
    }),
  );

  user: LoggedUser;

  isScrolled = false;
  isNextScrolled = false;
  allowExpandMenu = true;
  private displayScrollbar = false;
  readonly minScroll = 8;

  get tenants(): BaseModel[] {
    return this.tenantsService.tenants;
  }

  get selectedTenant(): BaseModel {
    return this.tenantsService.selectedTenant ?? (this.tenantsService.showWorkflows ? this.allWorkflows : this.allTenants);
  }

  get showAllWorkflows(): boolean {
    return this.authService.hasFeatureFlag(FeatureFlags.Workflows);
  }

  get isMobile(): boolean {
    return Global.isMobile;
  }

  get parentRoute$(): Observable<string[] | null> {
    return this.menuSettings$.pipe(map(m => m.sections[0]?.parentRoute));
  }

  get firstLabel$(): Observable<string | null> {
    return this.menuSettings$.pipe(map(m => m.sections[0]?.label));
  }

  get showTenants$(): Observable<boolean> {
    return this.menuSettings$.pipe(map(m => m.showTenants));
  }

  get rootRoute(): string[] {
    return this.navigationService.getRootRoute();
  }

  get isMenuExpanded(): boolean {
    return this.storageService.isMenuExpanded;
  }

  set isMenuExpanded(expanded: boolean) {
    this.storageService.isMenuExpanded = expanded;
  }

  get allowOrganizationSelect(): boolean {
    // depends on the number of organizations for the user
    return this.organizationService.organizations?.length > 1;
  }

  get isUserPasswordLoginEnabled(): boolean {
    return this.organizationService.organizationConfigurationUi?.isUserPasswordLoginDisabled != true;
  }

  get isSystemAdmin(): boolean {
    return this.authService.isSystemAdmin;
  }

  get areNotificationSettingsEnabled(): boolean {
    return this.authService.hasPermission(ClientPermission.MetricAlerts);
  }

  @ViewChild('menuBodyContainer', { static: false }) set menuBodyContainer(element: ElementRef<HTMLElement>) {
    this.isNextScrolled = element.nativeElement.offsetHeight < element.nativeElement.children[0].scrollHeight - this.minScroll;
    this.cdr.markForCheck();
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private menuService: MenuService,
    private modalService: ModalService,
    private authService: AuthService,
    private navigationService: NavigationService,
    private storageService: StorageService,
    private organizationService: OrganizationService,
    private tenantsService: TenantsService,
    private loadingScreenService: LoadingScreenService,
  ) {
    Global.deviceTypeHasChanged$.subscribe(() => {
      if (Global.deviceType === DeviceType.phone) {
        this.isMenuExpanded = false;
      }
      this.cdr.markForCheck();
    });

    this.tenantsService.tenants$.pipe(takeUntilDestroyed()).subscribe(() => {
      // Refresh menu when tenants change outside of this component, e.g. in organization settings
      this.cdr.markForCheck();
    });

    this.user = this.authService.user;
  }

  toggleMenuExpanded(): void {
    this.isMenuExpanded = !this.isMenuExpanded;
    if (!this.allowExpandMenu) {
      this.isMenuExpanded = false;
    }
    this.cdr.markForCheck();
  }

  onBodyContainerScroll(event: Event): void {
    const target = event.target as HTMLElement;
    // header scroll shadow
    this.isScrolled = target.scrollTop > this.minScroll;
    // footer scroll shadow
    this.isNextScrolled = target.offsetHeight + target.scrollTop <= target.scrollHeight - this.minScroll;
    if (this.isScrolled !== this.displayScrollbar) {
      this.displayScrollbar = this.isScrolled;
    }
  }

  async displayOrganizationSelect(): Promise<void> {
    const organizations = this.organizationService.organizations.map(o => ({
      id: o.name,
      label: o.label,
    } as BaseModel));
    const organizationRef = this.modalService.open(CustomSelectModalComponent<BaseModel>, d => {
      d.items = organizations;
      d.selectedItemId = this.organizationService.selectedOrganization?.name;
      d.selectTitle = 'Organizations';
    });

    try {
      const result = await organizationRef.result as BaseModel;
      if (!result?.id) {
        return; // User cancelled the selection
      }
      this.loadingScreenService.showLoadingScreen();
      await this.navigationService.navigateToOtherOrganization(result.id);
    } finally {
      this.loadingScreenService.hideLoadingScreen();
    }
  }

  changePassword(): void {
    this.modalService.open(ChangePasswordModalComponent);
  }

  async signOut(): Promise<void> {
    await this.navigationService.navigateToLogin('User logged out manually', false, false);
  }

  async gotoDesignSystemOverview(): Promise<void> {
    await this.navigationService.navigateToDesignSystemOverview();
  }

  async gotoAdministrationPage(): Promise<void> {
    await this.navigationService.navigateToAdminOrganization(this.organizationService.selectedOrganization?.name ?? '');
  }

  async reloadPage(route: string[]): Promise<void> {
    await this.navigationService.navigateByRoute(route, null, true);
  }

  openNotificationSettings(): void {
    this.modalService.open(NotificationSettingsModalComponent);
  }

  async onTenantChanged(tenant: BaseModel): Promise<void> {
    if (tenant !== this.selectedTenant) {
      await this.tenantsService.selectTenant(tenant.id);
    }
  }

  async openTenantSelect(): Promise<void> {
    const tenantRef = this.modalService.open(CustomSelectModalComponent<BaseModel>, d => {
      d.items = [this.allTenants, ...this.tenants];
      d.selectedItemId = this.selectedTenant?.id;
      d.selectTitle = 'Tenants';
    });

    const selectedTenant = await tenantRef.result as BaseModel;
    await this.onTenantChanged(selectedTenant);
  }

  isBadgeVisible(menuItem: MenuItem, badgeNumber: number): boolean {
    const hideWhenOnPage = menuItem.hideBadgeWhenOnPage === true && this.navigationService.isOnPage(menuItem.pageType);
    return badgeNumber > 0 && !hideWhenOnPage;
  }

  isMenuItemActive(menuItem: MenuItem): boolean {
    if (!menuItem.activeRoutes?.length) {
      return false;
    }

    const currentUrl = this.router.url;
    const result = menuItem.activeRoutes.some(route => {
      // Filter out empty strings and standalone '/' to avoid double slashes
      const routeUrl = '/' + route.filter(segment => segment && segment !== '/').join('/');
      return currentUrl.startsWith(routeUrl);
    });

    return result;
  }
}
